import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BooleanSearchRoundResponse,
  BooleanSearchRoundSave,
  BooleanSearchSavePayload,
} from "../api/BooleanSearch";
import { useBooleanSearchApi } from "../api/BooleanSearchApi";
import { useWebSearchJobStatus } from "../api/WebSearchAPI";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { countWords } from "../reports/utils";
import { classNames, uniqueUUID } from "../utils";
import { buildWebSearchWorkbenchNavigationState } from "./workbenchEntrySource";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";

const BOOLEAN_QUERY_WORD_LIMIT = 32;

export function BooleanSearchTab({
  reportId,
  runExternalId,
  isInitializing = false,
  onSubmitSuccess,
  completionAction,
  onStatusChange,
}: BooleanSearchTabProps) {
  const { fetchRounds, saveRounds, submitRounds } = useBooleanSearchApi();
  const { showPushToast } = useApplicationToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [rounds, setRounds] = useState<BooleanSearchRoundForm[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestRoundsRef = useRef<BooleanSearchRoundForm[]>(rounds);
  const hasDirtyRef = useRef(false);
  const dirtyVersionRef = useRef(0);
  const didReorderDuringDragRef = useRef(false);
  const draggedRoundIdRef = useRef<string | null>(null);
  const reportIdRef = useRef<string | null>(reportId ?? null);
  const loadRequestRef = useRef(0);
  const [dirtySignal, setDirtySignal] = useState(0);
  const [draggedRoundId, setDraggedRoundId] = useState<string | null>(null);
  const [dragOverRoundId, setDragOverRoundId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] =
    useState<DragOverPosition>("before");

  const {
    data: jobStatus,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    error: statusError,
  } = useWebSearchJobStatus(runExternalId ?? null, !!runExternalId);

  const status = jobStatus?.status?.toLowerCase() || "";
  const isRunningStatus =
    status === "running" || status === "partial" || status === "queued";
  const hasTerminalSearchError =
    !!runExternalId &&
    (status === "failed" || status === "cancelled" || status === "canceled");
  const isCheckingSearchStatus =
    !!runExternalId && isLoadingStatus && !jobStatus;
  const isCompleted = !!runExternalId && status === "completed";
  const isSearching =
    isStarting ||
    isCheckingSearchStatus ||
    (!!runExternalId && isRunningStatus);
  const isReadOnly = isSearching || isCompleted;

  useEffect(() => {
    latestRoundsRef.current = rounds;
  }, [rounds]);

  useEffect(() => {
    reportIdRef.current = reportId ?? null;
    hasDirtyRef.current = false;
    dirtyVersionRef.current = 0;
    draggedRoundIdRef.current = null;
    setHasFetched(false);
    setIsGenerated(false);
    setIsLoading(false);
    setIsSaving(false);
    setIsStarting(false);
    setStatusMessage("");
    setRounds([]);
    latestRoundsRef.current = [];
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, [reportId]);

  const loadRounds = useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    const activeReportId = reportIdRef.current;
    if (!activeReportId) return;
    setIsLoading(true);
    try {
      const response = await fetchRounds(activeReportId);
      if (
        reportIdRef.current !== activeReportId ||
        loadRequestRef.current !== requestId
      )
        return;
      const nextState = convertRoundsToForm(
        response,
        latestRoundsRef.current,
        7
      );
      latestRoundsRef.current = nextState;
      setRounds(nextState);
      setIsGenerated(true);
      hasDirtyRef.current = false;
      setStatusMessage("");
    } catch (error) {
      if (
        reportIdRef.current !== activeReportId ||
        loadRequestRef.current !== requestId
      )
        return;
      console.error("Failed to load boolean search rounds", error);
      const fallback = [createBlankRound()];
      latestRoundsRef.current = fallback;
      setRounds(fallback);
      setStatusMessage("Unable to generate Boolean rounds.");
    } finally {
      if (
        reportIdRef.current === activeReportId &&
        loadRequestRef.current === requestId
      ) {
        setIsLoading(false);
        setHasFetched(true);
      }
    }
  }, [fetchRounds]);

  useEffect(() => {
    if (!runExternalId || hasFetched || isLoading) return;
    void loadRounds();
  }, [hasFetched, isLoading, loadRounds, runExternalId]);

  const persistRounds = useCallback(
    async (
      nextRounds: BooleanSearchRoundForm[],
      saveFn: typeof saveRounds = saveRounds
    ): Promise<boolean> => {
      const activeReportId = reportIdRef.current;
      if (!activeReportId) return false;
      setIsSaving(true);
      const normalizedRounds = normalizeRounds(nextRounds);
      const saveStartedDirtyVersion = dirtyVersionRef.current;
      const payload: BooleanSearchSavePayload = {
        rounds: normalizedRounds.map((round) => mapRoundToSave(round)),
      };

      try {
        const response = await saveFn(activeReportId, payload);
        const nextState =
          Array.isArray(response) && response.length > 0
            ? mergeSavedRoundsIntoLocal(normalizedRounds, response)
            : normalizedRounds;
        if (reportIdRef.current === activeReportId) {
          const hasNewerLocalChanges =
            dirtyVersionRef.current !== saveStartedDirtyVersion;
          if (!hasNewerLocalChanges) {
            latestRoundsRef.current = nextState;
            setRounds(nextState);
            setStatusMessage(
              `Last saved ${new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`
            );
            hasDirtyRef.current = false;
          }
        }
        return true;
      } catch (error) {
        console.error("Failed to save boolean search rounds", error);
        hasDirtyRef.current = true;
        setStatusMessage("Unable to save Boolean rounds.");
        return false;
      } finally {
        if (reportIdRef.current === activeReportId) {
          setIsSaving(false);
        }
      }
    },
    [saveRounds]
  );

  const markDirty = useCallback(() => {
    if (isReadOnly) return;
    dirtyVersionRef.current += 1;
    hasDirtyRef.current = true;
    setDirtySignal((value) => value + 1);
  }, [isReadOnly]);

  const flushAutosave = useCallback(async (): Promise<AutosaveState> => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (!hasDirtyRef.current) return "synced";
    const result = await persistRounds(latestRoundsRef.current);
    return result ? "saved" : "failed";
  }, [persistRounds]);

  const handleRoundChange = useCallback(
    (roundId: string, changes: Partial<BooleanSearchRoundForm>) => {
      if (isReadOnly) return;
      setRounds((current) => {
        const next = current.map((round) =>
          round.client_id === roundId ? { ...round, ...changes } : round
        );
        latestRoundsRef.current = next;
        return next;
      });
      markDirty();
    },
    [isReadOnly, markDirty]
  );

  const handleAddRound = useCallback(() => {
    if (isReadOnly) return;
    setRounds((current) => {
      const next = normalizeRounds([...current, createBlankRound()]);
      latestRoundsRef.current = next;
      return next;
    });
    markDirty();
  }, [isReadOnly, markDirty]);

  const handleDeleteRound = useCallback(
    (roundId: string) => {
      if (isReadOnly) return;
      let didDelete = false;
      setRounds((current) => {
        const filtered = current.filter((round) => round.client_id !== roundId);
        if (filtered.length === current.length) return current;
        didDelete = true;
        const next = normalizeRounds(
          filtered.length ? filtered : [createBlankRound()]
        );
        latestRoundsRef.current = next;
        return next;
      });
      if (didDelete) {
        markDirty();
      }
    },
    [isReadOnly, markDirty]
  );

  const handleRoundDragStart = useCallback(
    (
      event: React.DragEvent<HTMLElement>,
      roundId: string,
      dragImageElement?: HTMLElement | null
    ) => {
      if (isReadOnly) return;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", roundId);
      if (dragImageElement) {
        const bounds = dragImageElement.getBoundingClientRect();
        event.dataTransfer.setDragImage(
          dragImageElement,
          Math.min(32, bounds.width / 2),
          Math.min(24, bounds.height / 2)
        );
      }
      didReorderDuringDragRef.current = false;
      draggedRoundIdRef.current = roundId;
      setDraggedRoundId(roundId);
    },
    [isReadOnly]
  );

  const handleRoundDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, roundId: string) => {
      const activeDraggedRoundId = draggedRoundIdRef.current;
      if (
        isReadOnly ||
        !activeDraggedRoundId ||
        activeDraggedRoundId === roundId
      )
        return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      const bounds = event.currentTarget.getBoundingClientRect();
      const nextPosition =
        event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
      setDragOverRoundId(roundId);
      setDragOverPosition(nextPosition);
      const currentRounds = latestRoundsRef.current;
      const nextRounds = reorderRounds(
        currentRounds,
        activeDraggedRoundId,
        roundId,
        nextPosition
      );
      if (nextRounds === currentRounds) return;
      didReorderDuringDragRef.current = true;
      dirtyVersionRef.current += 1;
      hasDirtyRef.current = true;
      latestRoundsRef.current = nextRounds;
      setRounds(nextRounds);
    },
    [isReadOnly]
  );

  const handleRoundDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>, targetRoundId: string) => {
      event.preventDefault();
      const activeDraggedRoundId = draggedRoundIdRef.current;
      if (
        isReadOnly ||
        !activeDraggedRoundId ||
        activeDraggedRoundId === targetRoundId
      )
        return;
      let didDropReorder = false;
      setRounds((current) => {
        const next = reorderRounds(
          current,
          activeDraggedRoundId,
          targetRoundId,
          dragOverPosition
        );
        if (next === current) return current;
        didDropReorder = true;
        latestRoundsRef.current = next;
        return next;
      });
      setDraggedRoundId(null);
      draggedRoundIdRef.current = null;
      setDragOverRoundId(null);
      setDragOverPosition("before");
      if (didReorderDuringDragRef.current || didDropReorder) {
        markDirty();
      }
      didReorderDuringDragRef.current = false;
    },
    [dragOverPosition, isReadOnly, markDirty]
  );

  const handleRoundDragEnd = useCallback(() => {
    if (didReorderDuringDragRef.current) {
      markDirty();
    }
    didReorderDuringDragRef.current = false;
    draggedRoundIdRef.current = null;
    setDraggedRoundId(null);
    setDragOverRoundId(null);
    setDragOverPosition("before");
  }, [markDirty]);

  useEffect(() => {
    if (!hasFetched || isReadOnly) return;
    if (!hasDirtyRef.current) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      void persistRounds(latestRoundsRef.current);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [dirtySignal, hasFetched, isReadOnly, persistRounds]);

  const hasRoundOverWordLimit = useMemo(
    () =>
      rounds.some(
        (round) => countWords(round.query_text ?? "") > BOOLEAN_QUERY_WORD_LIMIT
      ),
    [rounds]
  );
  const isStartSearchBlockedByWordLimit = !isCompleted && hasRoundOverWordLimit;

  const handleStartSearch = useCallback(async () => {
    if (isReadOnly) return;
    setIsStarting(true);
    try {
      const autosaveStatus = await flushAutosave();
      if (autosaveStatus === "failed") {
        showPushToast?.({
          message: "Unable to save Boolean rounds. Please try again.",
          type: "error",
          autoDismiss: true,
          duration: 5000,
        });
        setIsStarting(false);
        return;
      }

      const activeReportId = reportIdRef.current;
      if (!activeReportId) {
        onSubmitSuccess?.();
        return;
      }

      const normalizedRounds = normalizeRounds(latestRoundsRef.current);
      const payload: BooleanSearchSavePayload = {
        rounds: normalizedRounds.map((round) => mapRoundToSave(round)),
      };
      const webSearchResponse = await submitRounds(activeReportId, payload);
      const nextRunExternalId = webSearchResponse?.run_external_id;
      if (nextRunExternalId) {
        showPushToast?.({
          message: "Web search started successfully.",
          type: "success",
          autoDismiss: true,
          duration: 3000,
        });
      } else {
        showPushToast?.({
          message: "Failed to start web search. Please try again.",
          type: "error",
          autoDismiss: true,
          duration: 5000,
        });
      }
      onSubmitSuccess?.(nextRunExternalId);
    } catch (error) {
      console.error("Failed to start web search", error);
      showPushToast?.({
        message: "Failed to start web search. Please try again.",
        type: "error",
        autoDismiss: true,
        duration: 5000,
      });
      onSubmitSuccess?.();
    } finally {
      setIsStarting(false);
    }
  }, [flushAutosave, isReadOnly, onSubmitSuccess, showPushToast, submitRounds]);

  const handleViewResults = useCallback(() => {
    if (!runExternalId) return;
    navigate(`/360/${reportId}/web-results`, {
      state: {
        ...buildWebSearchWorkbenchNavigationState(location.state),
        runExternalId,
      },
    });
  }, [location.state, navigate, reportId, runExternalId]);

  const actionLabel = isCompleted
    ? "View Results"
    : isSearching
    ? "Searching"
    : "Start Search";

  const actionButtonClass = isCompleted
    ? "border-orange-500 bg-white text-orange-600 hover:bg-orange-50"
    : isSearching
    ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500"
    : "border-[#46b276] bg-white text-[#2f8f5b] hover:bg-green-50";
  const isActionDisabled =
    isLoading ||
    isSaving ||
    isStartSearchBlockedByWordLimit ||
    isInitializing ||
    (!isCompleted && isSearching);
  const visibleStatusMessage =
    isStatusError && statusError
      ? "Unable to check search status."
      : hasTerminalSearchError
      ? "Search failed. Review the Boolean rounds and start a new search."
      : statusMessage;
  const isLoadingWebSearchState =
    isInitializing ||
    (!!runExternalId && (!hasFetched || isLoading || isCheckingSearchStatus));
  const shouldShowToolbarButtons = !isLoadingWebSearchState;

  useEffect(() => {
    const nextStatus = isSaving
      ? "Saving..."
      : isStarting
      ? "Starting search..."
      : isStatusError && statusError
      ? "Unable to check search status."
      : hasTerminalSearchError
      ? "Search failed. Review the Boolean rounds and start a new search."
      : statusMessage;
    onStatusChange?.(nextStatus);
  }, [
    isSaving,
    isStarting,
    isStatusError,
    hasTerminalSearchError,
    onStatusChange,
    statusError,
    statusMessage,
  ]);

  const renderActionBar = (showSearchAction: boolean) => (
    <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-6 py-3">
      <div className="flex shrink-0 items-center gap-3">
        {showSearchAction && shouldShowToolbarButtons && (
          <button
            type="button"
            className={classNames(
              "inline-flex min-h-[34px] min-w-[108px] items-center justify-center rounded-md border px-3 py-1.5 text-sm font-semibold antialiased shadow-none transition disabled:cursor-not-allowed",
              actionButtonClass
            )}
            onClick={
              isCompleted ? handleViewResults : () => void handleStartSearch()
            }
            disabled={isActionDisabled}
            title={
              isStartSearchBlockedByWordLimit
                ? `Each round must be at most ${BOOLEAN_QUERY_WORD_LIMIT} words`
                : undefined
            }
          >
            {isSearching && (
              <LoadingSpinner className="mr-2 h-4 w-4 text-gray-500" />
            )}
            {actionLabel}
          </button>
        )}
      </div>
      <div className="ml-auto min-w-0 truncate text-right text-sm text-gray-500">
        {isSaving && "Saving..."}
        {!isSaving && visibleStatusMessage}
      </div>
      {completionAction && shouldShowToolbarButtons && (
        <div className="flex shrink-0 items-center justify-end">
          {completionAction}
        </div>
      )}
    </div>
  );

  const renderLoadingContent = () => (
    <div className="flex flex-1 items-center justify-center">
      <LoadingSpinner />
    </div>
  );

  if (!isGenerated && !runExternalId) {
    return (
      <section
        aria-label="360 Web Search"
        className="flex min-h-[460px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        {renderActionBar(false)}
        {isLoadingWebSearchState ? (
          renderLoadingContent()
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
            <h3 className="text-xl font-semibold text-gray-900">
              Generate Boolean Searches
            </h3>
            <p className="mt-2 max-w-md text-sm text-gray-600">
              Generate Boolean search queries for this report. You can review
              and adjust them before starting the web search.
            </p>
            <button
              type="button"
              className="mt-6 inline-flex min-h-[34px] items-center justify-center rounded-md bg-[#46b276] px-3 py-1.5 text-sm font-semibold text-white antialiased shadow-none transition hover:bg-[#379b63] disabled:cursor-not-allowed disabled:bg-gray-300"
              onClick={() => void loadRounds()}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner className="h-4 w-4" />
                  Generating...
                </span>
              ) : (
                "Generate Boolean Searches"
              )}
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      aria-label="360 Web Search"
      className="flex max-h-[calc(100vh-260px)] min-h-[460px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      {renderActionBar(true)}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {isLoadingWebSearchState ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {rounds.map((round) => (
                <RoundRow
                  key={round.client_id}
                  round={round}
                  totalRounds={rounds.length}
                  isReadOnly={isReadOnly}
                  isDragging={draggedRoundId === round.client_id}
                  isDragOver={dragOverRoundId === round.client_id}
                  onChange={(changes) =>
                    handleRoundChange(round.client_id, changes)
                  }
                  onDelete={() => handleDeleteRound(round.client_id)}
                  onDragStart={(event, dragImageElement) =>
                    handleRoundDragStart(
                      event,
                      round.client_id,
                      dragImageElement
                    )
                  }
                  onDragOver={(event) =>
                    handleRoundDragOver(event, round.client_id)
                  }
                  onDrop={(event) => handleRoundDrop(event, round.client_id)}
                  onDragEnd={handleRoundDragEnd}
                />
              ))}
            </div>
            {!isReadOnly && (
              <button
                type="button"
                className="mt-6 inline-flex items-center gap-2 text-base font-semibold text-blue-800 transition hover:text-blue-900"
                onClick={handleAddRound}
              >
                <PlusCircleIcon className="h-5 w-5" />
                Add Round
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function RoundRow({
  round,
  totalRounds,
  isReadOnly,
  isDragging,
  isDragOver,
  onChange,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: RoundRowProps) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const wordHintId = `boolean-search-round-${round.client_id}-wordcount`;
  const wordCount = countWords(round.query_text ?? "");
  const overWordLimit = wordCount > BOOLEAN_QUERY_WORD_LIMIT;
  const wordCountLabel = overWordLimit
    ? `Word Count: ${wordCount}/${BOOLEAN_QUERY_WORD_LIMIT}`
    : `Word Count: ${wordCount}`;

  const resizeTextarea = useCallback(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [round.query_text, resizeTextarea]);

  if (isReadOnly) {
    return (
      <div className="space-y-2 rounded-md">
        <div className="text-sm font-bold text-gray-700">
          Round {round.round_index}
        </div>
        <div className="min-w-0">
          <div className="w-full whitespace-pre-wrap break-words text-sm text-gray-900">
            {round.query_text || "No Boolean search query"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      className={classNames(
        "space-y-2 rounded-md transition-all duration-150",
        isDragging
          ? "scale-[0.99] opacity-80 shadow-sm ring-2 ring-ourGreen/20"
          : "",
        isDragOver ? "bg-gray-50 ring-2 ring-ourGreen/30" : ""
      )}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="text-sm font-medium text-gray-700">
        Round {round.round_index}
      </div>
      <div className="flex min-w-0 items-start gap-2">
        <DragHandle
          disabled={isReadOnly || totalRounds === 1}
          onDragStart={(event) => onDragStart(event, rowRef.current)}
          onDragEnd={onDragEnd}
          label={`Reorder round ${round.round_index}`}
        />
        <div className="min-w-0 flex-1">
          <textarea
            ref={textareaRef}
            id={`boolean-search-round-${round.client_id}-query`}
            className={classNames(
              "w-full rounded-md border bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline-none transition placeholder-gray-400 overflow-hidden",
              isReadOnly ? "cursor-not-allowed bg-gray-50 text-gray-600" : "",
              overWordLimit
                ? "border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-500/40"
                : "border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-ourGreen/40"
            )}
            readOnly={isReadOnly}
            rows={1}
            value={round.query_text ?? ""}
            placeholder={'"First Last" OR "Alias Alias"'}
            onChange={(event) => onChange({ query_text: event.target.value })}
            onInput={resizeTextarea}
            style={{ resize: "none", minHeight: "38px" }}
            aria-describedby={wordHintId}
            aria-invalid={overWordLimit}
          />
          <div className="mt-1 flex w-full justify-end">
            <p
              id={wordHintId}
              className={classNames(
                "text-right text-sm font-medium",
                overWordLimit ? "text-red-600" : "text-gray-900"
              )}
            >
              {wordCountLabel}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center text-gray-400 transition hover:text-gray-600 disabled:opacity-50"
          onClick={onDelete}
          disabled={isReadOnly || totalRounds === 1}
          aria-label={`Remove round ${round.round_index}`}
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function DragHandle({
  disabled,
  label,
  onDragStart,
  onDragEnd,
}: DragHandleProps) {
  return (
    <button
      type="button"
      draggable={!disabled}
      className={classNames(
        "mt-1 flex h-8 w-6 shrink-0 items-center justify-center rounded text-gray-400 transition hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-ourGreen/40",
        disabled
          ? "cursor-not-allowed opacity-40"
          : "cursor-grab active:cursor-grabbing"
      )}
      disabled={disabled}
      aria-label={label}
      onDragStart={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onDragStart(event);
      }}
      onDragEnd={onDragEnd}
    >
      <span className="grid grid-cols-2 gap-[2px]" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, index) => (
          <span key={index} className="h-1 w-1 rounded-full bg-current" />
        ))}
      </span>
    </button>
  );
}

function createBlankRound(): BooleanSearchRoundForm {
  return {
    client_id: uniqueUUID(),
    round_index: 1,
    query_text: "",
  };
}

function createBlankRounds(count: number): BooleanSearchRoundForm[] {
  return Array.from({ length: count }, (_, index) => ({
    ...createBlankRound(),
    round_index: index + 1,
  }));
}

function convertRoundsToForm(
  rounds: BooleanSearchRoundResponse[],
  previousRounds?: BooleanSearchRoundForm[],
  emptyRoundCount: number = 1
): BooleanSearchRoundForm[] {
  if (!rounds || rounds.length === 0) {
    return createBlankRounds(emptyRoundCount);
  }
  const previousById = new Map<number, string>();
  const previousBySignature = new Map<string, string>();
  if (previousRounds?.length) {
    previousRounds.forEach((round) => {
      if (typeof round.id === "number") {
        previousById.set(round.id, round.client_id);
      }
      previousBySignature.set(
        buildRoundSignature(round.round_index, round.query_text),
        round.client_id
      );
    });
  }
  return rounds
    .filter((round) => !round.is_deleted)
    .sort((a, b) => a.round_index - b.round_index)
    .map((round) => {
      const signature = buildRoundSignature(
        round.round_index,
        round.query_text
      );
      const existingClientId =
        (typeof round.id === "number"
          ? previousById.get(round.id)
          : undefined) ?? previousBySignature.get(signature);
      if (typeof round.id === "number") {
        previousById.delete(round.id);
      }
      if (existingClientId) {
        previousBySignature.delete(signature);
      }
      return {
        client_id: existingClientId ?? uniqueUUID(),
        id: round.id,
        external_id: round.external_id,
        round_index: round.round_index ?? 1,
        query_text: round.query_text ?? "",
        template_id: round.template_id ?? undefined,
      };
    });
}

function mergeSavedRoundsIntoLocal(
  localRounds: BooleanSearchRoundForm[],
  savedRounds: BooleanSearchRoundResponse[]
) {
  const savedById = new Map<number, BooleanSearchRoundResponse>();
  const savedByExternalId = new Map<string, BooleanSearchRoundResponse>();
  const savedBySignature = new Map<string, BooleanSearchRoundResponse>();
  savedRounds
    .filter((round) => !round.is_deleted)
    .forEach((round) => {
      if (typeof round.id === "number") savedById.set(round.id, round);
      if (round.external_id) savedByExternalId.set(round.external_id, round);
      savedBySignature.set(
        buildRoundSignature(round.round_index, round.query_text),
        round
      );
    });

  return normalizeRounds(localRounds).map((round) => {
    const saved =
      (typeof round.id === "number" ? savedById.get(round.id) : undefined) ??
      (round.external_id
        ? savedByExternalId.get(round.external_id)
        : undefined) ??
      savedBySignature.get(
        buildRoundSignature(round.round_index, round.query_text)
      );
    if (!saved) return round;

    return {
      ...round,
      id: saved.id,
      external_id: saved.external_id,
      template_id: saved.template_id ?? undefined,
    };
  });
}

function buildRoundSignature(index?: number, query?: string) {
  return `${index ?? -1}|${(query ?? "").trim()}`;
}

function normalizeRounds(rounds: BooleanSearchRoundForm[]) {
  return rounds.map((round, index) => ({
    ...round,
    round_index: index + 1,
  }));
}

function reorderRounds(
  rounds: BooleanSearchRoundForm[],
  draggedRoundId: string,
  targetRoundId: string,
  position: DragOverPosition
) {
  const fromIndex = rounds.findIndex(
    (round) => round.client_id === draggedRoundId
  );
  const targetIndex = rounds.findIndex(
    (round) => round.client_id === targetRoundId
  );
  if (fromIndex < 0 || targetIndex < 0 || fromIndex === targetIndex)
    return rounds;

  const next = [...rounds];
  const [draggedRound] = next.splice(fromIndex, 1);
  const targetIndexAfterRemoval = next.findIndex(
    (round) => round.client_id === targetRoundId
  );
  const insertionIndex =
    position === "after"
      ? targetIndexAfterRemoval + 1
      : targetIndexAfterRemoval;
  next.splice(insertionIndex, 0, draggedRound);
  if (hasSameRoundOrder(rounds, next)) return rounds;
  return normalizeRounds(next);
}

function hasSameRoundOrder(
  first: BooleanSearchRoundForm[],
  second: BooleanSearchRoundForm[]
) {
  return first.every(
    (round, index) => round.client_id === second[index]?.client_id
  );
}

function mapRoundToSave(round: BooleanSearchRoundForm): BooleanSearchRoundSave {
  const hasPersistedId = typeof round.id === "number";
  return {
    id: hasPersistedId ? round.id : undefined,
    external_id: round.external_id,
    round_index: round.round_index,
    query_text: round.query_text ?? "",
    template_id: round.template_id,
  };
}

export interface BooleanSearchTabProps {
  reportId: string;
  runExternalId?: string | null;
  isInitializing?: boolean;
  onSubmitSuccess?: (runExternalId?: string) => void;
  completionAction?: React.ReactNode;
  onStatusChange?: (status: string) => void;
}

interface BooleanSearchRoundForm extends BooleanSearchRoundSave {
  client_id: string;
}

interface RoundRowProps {
  round: BooleanSearchRoundForm;
  totalRounds: number;
  isReadOnly: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onChange: (changes: Partial<BooleanSearchRoundForm>) => void;
  onDelete: () => void;
  onDragStart: (
    event: React.DragEvent<HTMLElement>,
    dragImageElement?: HTMLElement | null
  ) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}

interface DragHandleProps {
  disabled: boolean;
  label: string;
  onDragStart: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
}

type AutosaveState = "saved" | "synced" | "failed";
type DragOverPosition = "before" | "after";
const AUTOSAVE_DELAY_MS = 5000;
